import { useMemo, useState } from 'react'
import styled from 'styled-components'
import { widgetFont } from '../../widgetTypography'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, LabelList } from 'recharts'
import useTranslation from '../../../../hooks/useTranslation'
import AnalyticsCard from './AnalyticsCard'
import { Headline, KpiRow } from './Kpi'
import { ChartBox, Legend, TooltipCard, axisProps } from './ChartParts'
import { bucketLabel, spansYears } from './period'
import useAnalyticsWidget from './useAnalyticsWidget'
import useElementSize from './useElementSize'
import { chartFont } from '../../widgetTypography'

/*
 * Totals per bucket, each split into the categories it is made of — tickets by
 * priority per week, orders by status per month.
 *
 *     stack: {
 *         value: 'tickets',             a grouped measure (its sources set `group`)
 *         segments: [{ id, label, color }],   bottom to top; `id` is the group's value
 *         otherLabel                    rows in a group no segment names are summed
 *     }                                 into one more segment, so the bar total holds
 *     normalize   true — start as 100 % bars
 *     toggle      true (default) — offer the switch between counts and 100 %
 *     totals      the bar total over each bar, when there is room
 *
 * Every bar is the sum of its segments, segment for segment, in every bucket;
 * the tooltip lists them with their share of the bar. Values are expected to be
 * zero or more — a measure that goes negative belongs in a waterfall.
 */

const Controls = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 0.35rem 0.75rem;
    flex-shrink: 0;
`

const Toggle = styled.div`
    display: inline-flex;
    border: 1px solid ${p => p.theme.table.border};
    border-radius: 999px;
    padding: 1px;
`

const ToggleButton = styled.button`
    font: inherit;
    ${widgetFont.caption}
    padding: 0.05rem 0.55rem;
    border: none;
    border-radius: 999px;
    cursor: pointer;
    background: ${p => p['aria-pressed'] ? p.theme.table.border : 'transparent'};
    color: ${p => p['aria-pressed'] ? p.theme.font.color : p.theme.font.label};

    &:focus-visible { outline: 2px solid ${p => p.theme.font.highlight}; outline-offset: 1px; }
`

const OTHER = '__other'

const StackedBarWidget = ({ type, data: config, labels }) => {
    const { t } = useTranslation()
    const w = useAnalyticsWidget(type, config)
    const { period, data, palette, format, locale } = w
    const [ref, size] = useElementSize()
    const [normalize, setNormalize] = useState(!!config.normalize)
    const [hidden, setHidden] = useState([])

    const stack = config.stack ?? {}
    const valueFormat = config.measures?.[stack.value]?.format ?? 'number'

    const { rows, segments } = useMemo(() => {
        if (!data.current) return { rows: [], segments: [] }
        const named = (stack.segments ?? []).map((s, i) => ({ ...s, id: String(s.id), name: t(s.label), color: palette.color(s.color, i) }))
        const known = new Set(named.map(s => s.id))
        let hasOther = false
        const yearSpan = spansYears(period.range)
        const rows = period.keys.map(key => {
            const groups = data.current.bucketGroups(stack.value, key)
            const row = { key, label: bucketLabel(key, period.grain, locale, { spansYears: yearSpan }), __total: 0 }
            for (const s of named) row[s.id] = 0
            row[OTHER] = 0
            for (const [g, v] of groups) {
                const id = known.has(String(g)) ? String(g) : OTHER
                if (id === OTHER && v) hasOther = true
                row[id] += v
                row.__total += v
            }
            return row
        })
        const segments = hasOther
            ? [...named, { id: OTHER, name: t(stack.otherLabel ?? 'analytics.other'), color: palette.tokens.neutral }]
            : named
        return { rows, segments }
    }, [data.current, stack, period, locale, palette, t])

    const visible = segments.filter(s => !hidden.includes(s.id))
    const chartRows = useMemo(() => rows.map(row => {
        const total = visible.reduce((sum, s) => sum + row[s.id], 0)
        if (!normalize) return { ...row, __shown: total }
        const out = { ...row, __shown: 100 }
        for (const s of visible) out[s.id] = total ? (row[s.id] / total) * 100 : 0
        return out
    }), [rows, visible, normalize])

    const compact = size.height < 120 || size.width < 220
    const showYAxis = !compact && size.width >= 340
    const barWidth = rows.length ? size.width / rows.length : 0
    const showTotals = config.totals && !normalize && !compact && barWidth >= 34

    const renderTooltip = ({ active, payload }) => {
        if (!active || !payload?.length) return null
        const row = rows.find(r => r.key === payload[0].payload.key)
        const total = visible.reduce((sum, s) => sum + row[s.id], 0)
        const items = [...visible].reverse().map(s => ({
            color: s.color,
            name: s.name,
            value: `${format(row[s.id], valueFormat)}${total ? ` · ${format((row[s.id] / total) * 100, 'percent')}` : ''}`,
        }))
        return <TooltipCard title={bucketLabel(row.key, period.grain, locale, { long: true })} rows={items}
            footer={<div style={{ marginTop: 4, fontWeight: 600 }}>{t('analytics.total')}: {format(total, valueFormat)}</div>} />
    }

    const lastVisible = visible[visible.length - 1]?.id

    return (
        <AnalyticsCard
            labels={labels}
            period={period}
            status={w.status}
            error={data.error}
            onRetry={data.refetch}
            headline={<Headline kpi={w.headline} formatValue={format} compareLabel={w.compareLabel} />}
            footer={<KpiRow kpis={w.kpis} formatValue={format} />}
        >
            <Controls>
                <Legend items={segments.map(s => ({ id: s.id, name: s.name, color: s.color }))} hidden={hidden}
                    onToggle={id => setHidden(h => h.includes(id) ? h.filter(x => x !== id) : [...h, id])} />
                {config.toggle !== false && (
                    <Toggle role="group" aria-label={t('analytics.stack.mode')}>
                        <ToggleButton type="button" aria-pressed={!normalize} onClick={() => setNormalize(false)}>{t('analytics.stack.absolute')}</ToggleButton>
                        <ToggleButton type="button" aria-pressed={normalize} onClick={() => setNormalize(true)}>100%</ToggleButton>
                    </Toggle>
                )}
            </Controls>
            <ChartBox>
                <div ref={ref}>
                    {size.width > 0 && size.height > 0 && (
                        <BarChart width={size.width} height={size.height} data={chartRows} accessibilityLayer
                            barCategoryGap={rows.length > 20 ? '18%' : '28%'}
                            margin={{ top: showTotals ? 18 : 6, right: 4, left: showYAxis ? 0 : 4, bottom: 0 }}>
                            {!compact && <CartesianGrid vertical={false} stroke={palette.grid} strokeDasharray="3 3" />}
                            <XAxis dataKey="label" {...axisProps(palette)} minTickGap={12} interval="preserveStartEnd" />
                            <YAxis hide={!showYAxis} width={48} {...axisProps(palette)}
                                domain={normalize ? [0, 100] : [0, 'auto']} allowDecimals={valueFormat !== 'integer'}
                                tickFormatter={v => normalize ? `${v}%` : format(v, valueFormat, { compact: true })} />
                            <Tooltip content={renderTooltip} cursor={{ fill: palette.track }} />
                            {visible.map(s => (
                                <Bar key={s.id} dataKey={s.id} name={s.name} stackId="stack" fill={s.color}
                                    radius={s.id === lastVisible ? [4, 4, 0, 0] : 0} isAnimationActive={false} maxBarSize={56}
                                    stroke={palette.surface} strokeWidth={visible.length > 1 ? 1 : 0}>
                                    {showTotals && s.id === lastVisible && (
                                        <LabelList dataKey="__shown" position="top" fontSize={chartFont.label} fill={palette.axis}
                                            formatter={v => (v ? format(v, valueFormat, { compact: true }) : '')} />
                                    )}
                                </Bar>
                            ))}
                        </BarChart>
                    )}
                </div>
            </ChartBox>
        </AnalyticsCard>
    )
}

export default StackedBarWidget
